import { useState, useEffect } from "react";
import CrmLayout from "@/components/feature/CrmLayout";
import {
  knowledgeArticles,
  suggestedArticles,
  kbStats,
  categoryIcons,
  categoryBadgeColors,
} from "@/mocks/knowledgeBaseData";
import type { KnowledgeArticle, ArticleCategory } from "@/mocks/knowledgeBaseData";
import { fetchApi } from "@/services/apiService";

type CategoryTab = "All" | ArticleCategory;

const categoryTabs: CategoryTab[] = ["All", "FAQ", "Property", "Pricing", "Process", "Contact"];

export default function KnowledgeBasePage() {
  const [articles, setArticles] = useState<KnowledgeArticle[]>(knowledgeArticles);
  const [activeTab, setActiveTab] = useState<CategoryTab>("All");
  const [showModal, setShowModal] = useState(false);
  const [editingArticle, setEditingArticle] = useState<KnowledgeArticle | null>(null);
  const [toast, setToast] = useState<{ message: string; type: "success" | "error" } | null>(null);
  const [expandedMenu, setExpandedMenu] = useState<string | null>(null);

  useEffect(() => {
    fetchApi<KnowledgeArticle[]>('/knowledge-base', undefined, knowledgeArticles)
      .then((data) => {
        if (data && data.length > 0) setArticles(data);
      })
      .catch((err) => console.error("Error fetching knowledge base articles:", err));
  }, []);

  const showToast = (message: string, type: "success" | "error" = "success") => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const filteredArticles = activeTab === "All" ? articles : articles.filter((a) => a.category === activeTab);
  const activeCount = articles.filter((a) => a.active).length;

  const toggleActive = async (id: string) => {
    const target = articles.find((a) => a.id === id);
    if (!target) return;
    const newStatus = !target.active;
    setArticles((prev) =>
      prev.map((a) => (a.id === id ? { ...a, active: newStatus } : a))
    );
    try {
      await fetchApi(`/knowledge-base/${id}`, {
        method: "PUT",
        body: JSON.stringify({ active: newStatus }),
      });
      showToast("Article status updated");
    } catch {
      showToast("Failed to update status", "error");
    }
  };

  const handleDelete = async (id: string) => {
    setArticles((prev) => prev.filter((a) => a.id !== id));
    setExpandedMenu(null);
    try {
      await fetchApi(`/knowledge-base/${id}`, { method: "DELETE" });
      showToast("Article deleted");
    } catch {
      showToast("Failed to delete article", "error");
    }
  };

  const handleAddEdit = (article?: KnowledgeArticle) => {
    setEditingArticle(article || null);
    setShowModal(true);
    setExpandedMenu(null);
  };

  const handleSaveArticle = async (article: KnowledgeArticle) => {
    try {
      if (editingArticle) {
        await fetchApi(`/knowledge-base/${article.id}`, {
          method: "PUT",
          body: JSON.stringify(article),
        });
        setArticles((prev) => prev.map((a) => (a.id === article.id ? article : a)));
        showToast("Article updated successfully");
      } else {
        const res = await fetchApi<any>("/knowledge-base", {
          method: "POST",
          body: JSON.stringify(article),
        });
        const savedItem = res?.data || { ...article, id: `ka-${Date.now()}`, usedCount: 0 };
        setArticles((prev) => [savedItem, ...prev]);
        showToast("Article created successfully");
      }
    } catch (err: any) {
      showToast(err.message || "Failed to save article", "error");
    } finally {
      setShowModal(false);
      setEditingArticle(null);
    }
  };

  return (
    <CrmLayout title="Knowledge Base">
      <div className="p-5 md:p-6 space-y-5 max-w-5xl">
        {/* Page Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900">Knowledge Base</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Add FAQs and product info so your bot can answer lead questions before handing over to your team
            </p>
          </div>
          <button
            onClick={() => handleAddEdit()}
            className="flex items-center gap-1.5 px-4 py-2.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line" /> Add Article
          </button>
        </div>

        {/* Info Banner */}
        <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex gap-3">
          <div className="w-10 h-10 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <i className="ri-robot-2-line text-indigo-600 text-lg w-5 h-5 flex items-center justify-center" />
          </div>
          <div>
            <p className="text-sm text-gray-700">
              Your bot <strong>Aria</strong> uses this knowledge base to answer questions automatically. When a question is outside the KB, bot flags for human handover.
            </p>
            <p className="text-xs text-gray-400 mt-1">Last updated: 2 hours ago</p>
          </div>
        </div>

        {/* Stats Row */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-article-line text-green-600 text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{articles.length}</p>
                <p className="text-xs text-gray-500">Articles</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-chat-check-line text-indigo-600 text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{kbStats.botAnswered.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Bot Answered</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl border border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center flex-shrink-0">
                <i className="ri-flag-line text-amber-600 text-lg w-5 h-5 flex items-center justify-center" />
              </div>
              <div>
                <p className="text-xl font-bold text-gray-900">{kbStats.flaggedToHuman.toLocaleString()}</p>
                <p className="text-xs text-gray-500">Flagged to Human</p>
              </div>
            </div>
          </div>
        </div>

        {/* Articles List */}
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between flex-wrap gap-3">
            <div className="flex items-center gap-2">
              <h2 className="text-sm font-semibold text-gray-900">Knowledge Articles</h2>
              <span className="px-2 py-0.5 rounded-full text-[11px] font-semibold bg-indigo-50 text-indigo-700">
                {activeCount}
              </span>
            </div>
            <button
              onClick={() => handleAddEdit()}
              className="text-xs text-indigo-600 hover:text-indigo-800 font-medium whitespace-nowrap cursor-pointer"
            >
              + Add Article
            </button>
          </div>

          {/* Filter Tabs */}
          <div className="px-5 pt-3 pb-1 flex gap-1 overflow-x-auto">
            {categoryTabs.map((tab) => (
              <button
                key={tab}
                onClick={() => setActiveTab(tab)}
                className={`px-4 py-2 text-sm rounded-lg transition-colors whitespace-nowrap cursor-pointer font-medium ${
                  activeTab === tab
                    ? "border-b-2 border-indigo-600 text-indigo-600"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                {tab}
              </button>
            ))}
          </div>

          {/* Articles Rows */}
          <div>
            {filteredArticles.map((article) => {
              const catIcon = categoryIcons[article.category];
              return (
                <div
                  key={article.id}
                  className={`px-5 py-4 border-b border-gray-100 hover:bg-gray-50 transition-colors flex items-center justify-between gap-4 ${
                    !article.active ? "opacity-60 bg-gray-50" : ""
                  }`}
                >
                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${catIcon.bg}`}>
                      <i className={`${catIcon.icon} ${catIcon.text} text-base w-4 h-4 flex items-center justify-center`} />
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">{article.title}</p>
                      <p className="text-xs text-gray-500 truncate mt-0.5">
                        {article.content.length > 80 ? article.content.substring(0, 80) + "..." : article.content}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className={`px-2 py-0.5 rounded-full text-[11px] font-medium ${categoryBadgeColors[article.category]}`}>
                      {article.category}
                    </span>
                    <span className="text-xs text-gray-400 whitespace-nowrap">Used {article.usedCount} times</span>

                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        className="sr-only peer"
                        checked={article.active}
                        onChange={() => toggleActive(article.id)}
                      />
                      <div className="w-9 h-4.5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-3.5 after:w-3.5 after:transition-all peer-checked:bg-indigo-600" />
                    </label>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleAddEdit(article)}
                        className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-indigo-600 hover:bg-indigo-50 transition-colors cursor-pointer"
                        title="Edit"
                      >
                        <i className="ri-pencil-line text-sm w-3.5 h-3.5 flex items-center justify-center" />
                      </button>
                      <button
                        onClick={() => handleDelete(article.id)}
                        className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        title="Delete"
                      >
                        <i className="ri-delete-bin-line text-sm w-3.5 h-3.5 flex items-center justify-center" />
                      </button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI-Suggested FAQs */}
        <div className="bg-white rounded-xl border border-gray-200 p-5">
          <h2 className="text-base font-semibold text-gray-900">Suggested Articles</h2>
          <p className="text-sm text-gray-500 mt-0.5 mb-4">Questions leads asked that the bot couldn&apos;t answer</p>

          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-3 mb-4">
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
              <i className="ri-lightbulb-fill text-amber-600" />
            </div>
            <p className="text-sm text-amber-800">
              These questions came from real lead conversations. Add answers to improve your bot&apos;s response rate.
            </p>
          </div>

          <div className="space-y-3">
            {suggestedArticles.map((suggestion) => (
              <div key={suggestion.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center justify-between gap-4">
                <div className="flex items-center gap-3 min-w-0">
                  <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-lightbulb-line text-amber-600 text-sm w-4 h-4 flex items-center justify-center" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-xs text-gray-400">Asked {suggestion.askedCount} times by leads:</p>
                    <p className="text-sm font-medium text-gray-900">{suggestion.question}</p>
                  </div>
                </div>
                <button
                  onClick={() => {
                    handleAddEdit();
                    showToast("Opening article editor — fill in the details for: " + suggestion.question);
                  }}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium text-indigo-600 border border-indigo-300 hover:bg-indigo-50 transition-colors whitespace-nowrap cursor-pointer flex-shrink-0"
                >
                  Create Article
                </button>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Add/Edit Article Modal */}
      {showModal && (
        <ArticleModal
          article={editingArticle}
          onClose={() => { setShowModal(false); setEditingArticle(null); }}
          onSave={handleSaveArticle}
        />
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-5 right-5 z-[100] px-4 py-3 rounded-lg shadow-sm text-sm font-medium animate-slide-in bg-green-500 text-white">
          <div className="flex items-center gap-2.5">
            <i className="ri-check-line text-base" />
            {toast.message}
          </div>
        </div>
      )}
    </CrmLayout>
  );
}

/* ---- Article Modal ---- */
function ArticleModal({
  article,
  onClose,
  onSave,
}: {
  article: KnowledgeArticle | null;
  onClose: () => void;
  onSave: (a: KnowledgeArticle) => void;
}) {
  const [title, setTitle] = useState(article?.title || "");
  const [category, setCategory] = useState<ArticleCategory>(article?.category || "FAQ");
  const [content, setContent] = useState(article?.content || "");
  const [keywords, setKeywords] = useState<string[]>(article?.keywords || []);
  const [keywordInput, setKeywordInput] = useState("");
  const [active, setActive] = useState(article?.active ?? true);

  const addKeyword = () => {
    const trimmed = keywordInput.trim().toLowerCase();
    if (trimmed && !keywords.includes(trimmed)) {
      setKeywords([...keywords, trimmed]);
    }
    setKeywordInput("");
  };

  const removeKeyword = (kw: string) => {
    setKeywords(keywords.filter((k) => k !== kw));
  };

  const handleSubmit = () => {
    if (!title.trim() || !content.trim()) return;
    onSave({
      id: article?.id || "",
      title: title.trim(),
      category,
      content: content.trim(),
      keywords,
      usedCount: article?.usedCount || 0,
      active,
    });
  };

  const previewContent = content.length > 120 ? content.substring(0, 120) + "..." : content;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-start justify-center pt-[8vh] px-4 overflow-y-auto">
        <div className="bg-white rounded-xl border border-gray-200 shadow-xl w-full max-w-2xl" onClick={(e) => e.stopPropagation()}>
          {/* Modal Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {article ? "Edit Knowledge Article" : "Add Knowledge Article"}
            </h3>
            <button onClick={onClose} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors cursor-pointer">
              <i className="ri-close-line text-lg w-5 h-5 flex items-center justify-center" />
            </button>
          </div>

          {/* Modal Body */}
          <div className="p-6 space-y-5 max-h-[70vh] overflow-y-auto">
            {/* Title */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Article Title <span className="text-red-500">*</span></label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. What are payment terms?"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
              />
            </div>

            {/* Category */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <select
                value={category}
                onChange={(e) => setCategory(e.target.value as ArticleCategory)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none bg-white"
              >
                <option value="FAQ">FAQ</option>
                <option value="Property">Property Info</option>
                <option value="Pricing">Pricing</option>
                <option value="Process">Process</option>
                <option value="Contact">Contact Info</option>
              </select>
            </div>

            {/* Content */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Content <span className="text-red-500">*</span></label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Write the full answer here. The bot will use this to respond to leads."
                rows={8}
                maxLength={2000}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none resize-none"
              />
              <p className="text-xs text-gray-400 mt-1 text-right">{content.length} / 2000</p>
            </div>

            {/* Keywords */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Keywords (for matching)</label>
              <div className="flex items-center gap-2 mb-2">
                <input
                  type="text"
                  value={keywordInput}
                  onChange={(e) => setKeywordInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addKeyword(); } }}
                  placeholder="e.g. payment, loan, EMI"
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                />
                <button
                  onClick={addKeyword}
                  className="px-3 py-2 rounded-lg bg-gray-100 hover:bg-gray-200 text-gray-600 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
                >
                  Add
                </button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {keywords.map((kw) => (
                  <span key={kw} className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700">
                    {kw}
                    <button onClick={() => removeKeyword(kw)} className="w-3.5 h-3.5 flex items-center justify-center hover:text-indigo-900 cursor-pointer">
                      <i className="ri-close-line text-[10px]" />
                    </button>
                  </span>
                ))}
              </div>
            </div>

            {/* Bot Preview */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Bot Preview</label>
              <div className="bg-gray-900 rounded-xl p-4">
                <p className="text-xs text-gray-400 font-medium mb-2">How bot will use this:</p>
                <div className="flex flex-col gap-2">
                  {/* Lead bubble */}
                  <div className="flex justify-start">
                    <div className="bg-white rounded-xl rounded-bl-sm px-3 py-2 max-w-[80%]">
                      <p className="text-sm text-gray-800">{title || "What is the payment process?"}</p>
                    </div>
                  </div>
                  {/* Bot bubble */}
                  <div className="flex justify-end">
                    <div className="bg-indigo-600 rounded-xl rounded-br-sm px-3 py-2 max-w-[80%]">
                      <p className="text-sm text-white">{previewContent || "Bot will respond with the article content here..."}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center justify-between py-2 border-t border-gray-100">
              <span className="text-sm font-medium text-gray-700">Article is active</span>
              <label className="relative inline-flex items-center cursor-pointer">
                <input type="checkbox" className="sr-only peer" checked={active} onChange={() => setActive(!active)} />
                <div className="w-10 h-5 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full rtl:peer-checked:after:-translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:start-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600" />
              </label>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm font-medium hover:bg-gray-50 transition-colors whitespace-nowrap cursor-pointer"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={!title.trim() || !content.trim()}
              className={`px-4 py-2 rounded-lg text-white text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                title.trim() && content.trim() ? "bg-indigo-600 hover:bg-indigo-700" : "bg-indigo-300 cursor-not-allowed"
              }`}
            >
              Save Article
            </button>
          </div>
        </div>
      </div>
    </>
  );
}